import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import List from 'components/AssetList';
import Header from 'components/AssetList/Header';
import { useToggle } from 'components/AssetList/hooks';
import Button from 'components/Button';
import { IconType } from 'components/Icon/constants';
import { EntityType, ModalType } from 'const';
import { toggleUpdatedDocumentsDisplaying as toggleUpdatedDocumentsDisplayingAction } from 'containers/App/actions';
import * as AppModels from 'containers/App/models';
import { showUpdatedReferences as showUpdatedReferencesSelector } from 'containers/App/selectors';
import { showModal } from 'containers/ModalManager/actions';
import { ReferenceCitationMap, ReferenceCitationsMap } from 'models';
import { intlGet } from 'utils/intlGet';
import CitationItem from '../CitationItem';
import styles from './styles.module.scss';

type Props = {
  citations: ReferenceCitationsMap;
  isExpand?: boolean;
  storyCardId?: string;
  toggleExpand?(): void;
  updatedInPromoMats?: boolean;
  dataTestId?: string;
};

const CitationList: React.FunctionComponent<Props> = (props) => {
  const {
    citations,
    isExpand,
    storyCardId,
    toggleExpand,
    updatedInPromoMats,
    dataTestId = 'citationList',
  } = props;

  const dispatch = useDispatch();
  const showUpdatedReferences = useSelector(showUpdatedReferencesSelector);
  const showModalHandler = (): void => {
    dispatch(showModal(ModalType.ADD_REFERENCE_CITATION));
  };
  const toggleUpdatedDocumentsDisplaying: AppModels.ActionCreator.ToggleUpdatedDocumentsDisplaying = (show, entityType?) => {
    return dispatch(toggleUpdatedDocumentsDisplayingAction(show, entityType));
  };

  const {
    expanded,
    toggle,
  } = useToggle({
    toggleUpdatedDocumentsDisplaying,
    showUpdatedDocuments: showUpdatedReferences,
    entityType: EntityType.REFERENCE_CITATION,
    toggle: toggleExpand,
    isExpand,
  });

  const renderSubheader = (): JSX.Element => (
    <div className={styles.subheader} data-testid={`${dataTestId}-list-subheader`}>
      <Button
        onClick={showModalHandler}
        size="md"
        title={intlGet('CitationsList', 'AddNew')}
        icon={IconType.PLUS_FILLED}
        dataTestId={`${dataTestId}-list-subheader-button`}
      />
    </div>
  );

  return (
    <div className={styles.CitationListContainer} data-title="reference-citations" data-testid={dataTestId}>
      {
        !storyCardId &&
        <Header
          title={intlGet('Assets', 'ReferenceCitations', { count: citations ? citations.size : 0 })}
          showUpdatesHint={updatedInPromoMats && showUpdatedReferences}
          expanded={expanded}
          onClick={toggle}
          dataTestId={`${dataTestId}-header`}
        />
      }
      {
        (expanded || storyCardId) &&
        <List
          border={!storyCardId}
          elements={citations}
          subheader={!storyCardId && renderSubheader()}
          renderElement={(citation: ReferenceCitationMap, index: number): React.ReactNode => (
            <CitationItem
              allowDelete={!storyCardId}
              citation={citation}
              key={citation.get('id')}
              dataTestId={`${dataTestId}-list-item${index}`}
            />
          )}
          dataTestId={`${dataTestId}-list`}
        />
      }
    </div>
  );
};

export default CitationList;
